import LinkIcon from "@mui/icons-material/Link";
import {
  FormControlLabel,
  Grid,
  Link,
  Radio,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useId } from "react";
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister,
} from "react-hook-form";
import {
  CreateCardRequest,
  EditCardRequest,
} from "tng-address-collection-shared/types/apiTypes";
import { useDepartmentNames } from "../hooks/useDepartmentNames";
import { useTranslator } from "../i18n/translator";
import { AlertBanner } from "./AlertBanner";
import { AutocompleteFreeSoloFormField } from "./AutocompleteFreeSoloFormField";
import { EmailDuplicateState } from "./CardFormFields";
import { describedBy } from "./describedBy.ts";
import { InfoToolTip } from "./InfoTooltip";
import { RadioGroupFormField } from "./RadioGroupFormField";

const titleOptions = [
  "Dr.",
  "Prof.",
  "Prof. Dr.",
  "Dipl.-Ing.",
  "Univ.-Prof.",
  "Dr. Ing.",
  "Mag.",
  "Ing.",
  "Dr. med.",
];

interface RecipientFieldsProps<T extends FieldValues> {
  isNameDuplicate: boolean;
  control: Control<T>;
  isFieldsDisabled: boolean;
  register: UseFormRegister<T>;
  emailDuplicateState: EmailDuplicateState;
  currentPreferredLanguage: string;
  projectId: string;
  responsibleProjectId?: string;
}

export const RecipientFields = ({
  isNameDuplicate,
  control,
  isFieldsDisabled,
  register,
  emailDuplicateState,
  currentPreferredLanguage,
  projectId,
  responsibleProjectId,
}: RecipientFieldsProps<CreateCardRequest | EditCardRequest>) => {
  const translator = useTranslator();
  const { data: departmentNames } = useDepartmentNames(projectId);
  const emailHelpId = useId();
  const commentHelpId = useId();
  const formalityHelpId = useId();
  const salutationHelpId = useId();
  return (
    <Stack spacing={2}>
      <Grid container columns={15} spacing={1}>
        <Grid size={3}>
          <AutocompleteFreeSoloFormField
            name="recipient.title"
            control={control}
            label={translator("title")}
            options={titleOptions}
            disabled={isFieldsDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Controller
            name="recipient.firstName"
            control={control}
            disabled={isFieldsDisabled}
            rules={{ required: translator("firstNameRequired") }}
            render={({ field, fieldState }) => (
              <TextField
                label={translator("firstName")}
                {...field}
                {...register("recipient.firstName")}
                value={field.value ?? ""}
                variant="outlined"
                fullWidth
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ??
                  (isNameDuplicate ? translator("errorCardNameNotUnique") : "")
                }
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                required
              />
            )}
          />
        </Grid>

        <Grid size={6}>
          <Controller
            name="recipient.lastName"
            control={control}
            disabled={isFieldsDisabled}
            rules={{ required: translator("lastNameRequired") }}
            render={({ field, fieldState }) => (
              <TextField
                label={translator("lastName")}
                {...field}
                value={field.value ?? ""}
                variant="outlined"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                required
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name="recipient.email"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label={
              <span>
                {translator("email")}
                <InfoToolTip
                  helpertext={translator("emailHelperText")}
                  ariaDescribedById={emailHelpId}
                />
              </span>
            }
            {...field}
            value={field.value ?? ""}
            variant="outlined"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message || ""}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                "aria-describedby": describedBy(
                  emailHelpId,
                  !!fieldState.error?.message && `${emailHelpId}-error`,
                ),
              },
              formHelperText: { id: `${emailHelpId}-error` },
            }}
          />
        )}
      />
      {emailDuplicateState.status === "duplicate-shareable" && (
        <AlertBanner
          message={
            <>
              {translator("shareCardWarning")}{" "}
              <Tooltip
                title={translator("openResponsibleProject")}
                placement="bottom"
                arrow
                disableInteractive
              >
                <Link
                  href={`/projects/${responsibleProjectId}`}
                  aria-label={translator("openResponsibleProject")}
                >
                  <LinkIcon
                    fontSize="small"
                    color="primary"
                    sx={{ verticalAlign: "middle" }}
                  />
                </Link>
              </Tooltip>
              {". "}
              {translator("shareCardOption")}
            </>
          }
          actionLabel={translator("shareCard")}
          onAction={emailDuplicateState.onShare}
          actionDisabled={emailDuplicateState.isSharing}
        />
      )}
      {emailDuplicateState.status !== "duplicate-shareable" && (
        <>
          <Controller
            name="recipient.companyName"
            control={control}
            disabled={isFieldsDisabled}
            render={({ field, fieldState }) => (
              <TextField
                label={translator("companyName")}
                {...field}
                value={field.value ?? ""}
                variant="outlined"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />

          <AutocompleteFreeSoloFormField
            name="departmentName"
            control={control}
            disabled={isFieldsDisabled}
            label={translator("departmentName")}
            options={departmentNames}
          />

          <Controller
            name="comment"
            control={control}
            disabled={isFieldsDisabled}
            render={({ field, fieldState }) => (
              <TextField
                label={
                  <span>
                    {translator("comment")}
                    <InfoToolTip
                      helpertext={translator("commentHelperText")}
                      ariaDescribedById={commentHelpId}
                    />
                  </span>
                }
                {...field}
                multiline
                value={field.value ?? ""}
                variant="outlined"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  htmlInput: {
                    "aria-describedby": describedBy(
                      commentHelpId,
                      !!fieldState.error?.message && `${commentHelpId}-error`,
                    ),
                  },
                  formHelperText: { id: `${commentHelpId}-error` },
                }}
              />
            )}
          />

          <Stack>
            <RadioGroupFormField
              name="recipient.preferredLanguage"
              control={control}
              label={translator("languageLabel")}
            >
              <FormControlLabel
                value="en"
                label={translator("english")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
              <FormControlLabel
                value="de"
                label={translator("german")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
            </RadioGroupFormField>

            <RadioGroupFormField
              name="formality"
              control={control}
              ariaDescribedBy={formalityHelpId}
              label={
                <span>
                  {translator("formalityLabel")}
                  <InfoToolTip
                    helpertext={
                      currentPreferredLanguage === "de"
                        ? translator("formalityHelperTextDE")
                        : translator("formalityHelperTextEN")
                    }
                    ariaDescribedById={formalityHelpId}
                  />
                </span>
              }
            >
              <FormControlLabel
                value="formal"
                label={translator("formal")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
              <FormControlLabel
                value="semi-formal"
                label={translator("semiFormal")}
                control={<Radio />}
                disabled={currentPreferredLanguage !== "de"}
              />
              <FormControlLabel
                value="informal"
                label={translator("informal")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
            </RadioGroupFormField>

            <RadioGroupFormField
              name="recipient.salutationKind"
              control={control}
              ariaDescribedBy={salutationHelpId}
              label={
                <span>
                  {translator("salutation")}
                  <InfoToolTip
                    helpertext={translator("salutationHelperText")}
                    ariaDescribedById={salutationHelpId}
                  />
                </span>
              }
            >
              <FormControlLabel
                value="male"
                label={translator("male")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
              <FormControlLabel
                value="female"
                label={translator("female")}
                control={<Radio />}
                disabled={isFieldsDisabled}
              />
            </RadioGroupFormField>
          </Stack>
        </>
      )}
    </Stack>
  );
};
